import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Table, Typography, Row, Col, Result, Spin, Tag, Select, Modal } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { SearchOutlined, UserOutlined, TeamOutlined, BankOutlined, DownloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import PublicAssetDetail from './PublicAssetDetail';
import * as XLSX from 'xlsx';
import { removeVietnameseTones } from '../utils/removeVietnameseTones';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

interface Asset {
  id: number;
  asset_code: string;
  name: string;
  category_name?: string;
  location_name?: string;
  status: string;
  purchase_cost?: number;
  public_uuid: string;
}

interface Employee {
  id: number;
  full_name: string;
  employee_code: string;
  department?: string;
  company_name?: string;
}

const EmployeeInfoCard: React.FC<{ employee: Employee }> = ({ employee }) => (
  <Card style={{ borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
    <Row gutter={[16, 16]} align="middle">
      <Col xs={24} md={8}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 12 }} />
          <div>
            <Text strong style={{ fontSize: 18 }}>{employee.full_name}</Text>
            <br />
            <Text type="secondary">Mã: {employee.employee_code}</Text>
          </div>
        </div>
      </Col>
      <Col xs={24} md={8}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TeamOutlined style={{ fontSize: 24, color: '#52c41a', marginRight: 12 }} />
          <div>
            <Text type="secondary">Phòng ban: {employee.department || '—'}</Text>
          </div>
        </div>
      </Col>
      <Col xs={24} md={8}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BankOutlined style={{ fontSize: 24, color: '#722ed1', marginRight: 12 }} />
          <div>
            <Text type="secondary">Nhân viên thuộc công ty: {employee.company_name || '—'}</Text>
          </div>
        </div>
      </Col>
    </Row>
  </Card>
);

const ExportExcelButtons: React.FC<{
  onExportAll: () => void;
  onExportSelected: () => void;
  disabledAll: boolean;
  disabledSelected: boolean;
}> = ({ onExportAll, onExportSelected, disabledAll, disabledSelected }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
    <Button icon={<DownloadOutlined />} style={{ marginRight: 8 }} onClick={onExportAll} disabled={disabledAll}>Xuất Excel tất cả</Button>
    <Button icon={<DownloadOutlined />} type="primary" onClick={onExportSelected} disabled={disabledSelected}>Xuất Excel đã chọn</Button>
  </div>
);

const cardStyle: React.CSSProperties = { borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' };
const cardTitleStyle: React.CSSProperties = { marginBottom: 8 };

const EmployeeAssetSearch: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [selectedAssetUuid, setSelectedAssetUuid] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const normText = (s: string) => s.normalize('NFC');

  useEffect(() => {
    setCompanyLoading(true);
    fetch('/api/companies/public')
      .then(res => res.json())
      .then(data => Array.isArray(data) ? setCompanies(data) : setCompanies([]))
      .catch(() => setCompanies([]))
      .finally(() => setCompanyLoading(false));
  }, []);

  const getStatusName = (status: string) => ({
    pending_deployment: 'Chờ triển khai', in_use: 'Đang sử dụng', in_storage: 'Đang lưu kho', under_maintenance: 'Đang bảo dưỡng', disposed: 'Đã thanh lý'
  } as any)[status] || status;
  const getStatusColor = (status: string) => ({
    pending_deployment: 'orange', in_use: 'green', in_storage: 'blue', under_maintenance: 'purple', disposed: 'red'
  } as any)[status] || 'default';
  const formatCurrency = (amt: any) => {
    const num = Number(amt);
    return num ? num.toLocaleString('vi-VN') + ' VNĐ' : '—';
  };

  const handleSearch = async () => {
    const query = normText(searchValue.trim());
    if (!query) return;

    if (!companyId) {
      Modal.warning({ title: 'Vui lòng chọn công ty', content: 'Bạn cần chọn công ty trước khi tra cứu nhân viên.' });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/employees/search?query=${encodeURIComponent(query)}&company_id=${companyId}`);
      const data = await res.json();
      if (data && data.length) {
        const emp = data[0];
        setEmployee(emp);
        const assetRes = await fetch(`/api/assets/public/assets-by-employee?employee_id=${emp.id}`);
        const assetData = await assetRes.json();
        setAssets(assetData.data || assetData);
      } else {
        setEmployee(null);
        setAssets([]);
      }
    } catch {
      setEmployee(null);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Mã tài sản', dataIndex: 'asset_code', key: 'asset_code', width: '15%', sorter: (a: Asset, b: Asset) => (a.asset_code || '').localeCompare(b.asset_code || ''), defaultSortOrder: 'descend' as SortOrder,
      render: (text: string, record: Asset) => <a style={{ color: '#1890ff', fontWeight: 'bold' }} onClick={() => { setSelectedAssetUuid(record.public_uuid); setAssetModalOpen(true); }}>{text}</a>
    },
    { title: 'Tên tài sản', dataIndex: 'name', key: 'name', width: '25%', render: (t: string) => <Text strong>{t}</Text> },
    { title: 'Danh mục', dataIndex: 'category_name', key: 'category_name', width: '15%', render: (t: string) => t || '—' },
    { title: 'Vị trí', dataIndex: 'location_name', key: 'location_name', width: '15%', render: (t: string) => t || '—' },
    { title: 'Trạng thái', dataIndex: 'status', key: 'status', width: '15%', render: (s: string) => <Tag color={getStatusColor(s)}>{getStatusName(s)}</Tag> },
    { title: 'Giá trị', dataIndex: 'purchase_cost', key: 'purchase_cost', width: '15%', align: 'right' as const, render: (c: number) => <Text strong style={{ color: '#52c41a' }}>{formatCurrency(c)}</Text> }
  ];

  const rowSelection = { selectedRowKeys, onChange: (k: React.Key[]) => setSelectedRowKeys(k) };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)', padding: 20 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', minHeight: 'calc(100vh - 60px)', paddingBottom: 60 }}>
        <Link to="/login" style={{ position: 'absolute', top: 0, right: 0 }}>
          <button style={{ background: '#1677ff', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, margin: 16 }}>Quay về đăng nhập</button>
        </Link>
        <div style={{ textAlign: 'center', marginBottom: 32, color: 'white' }}>
          <Title level={1} style={{ color: '#fff', marginBottom: 8 }}>Tra cứu tài sản nhân viên</Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16 }}>Nhập tên hoặc mã nhân viên và chọn công ty để tra cứu tài sản được giao</Text>
        </div>
        <Card style={{ ...cardStyle, marginBottom: 32 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <Select showSearch placeholder="Chọn công ty" value={companyId} onChange={setCompanyId} loading={companyLoading} optionFilterProp="children" style={{ width: '100%' }}>
                {companies.map(c => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
              </Select>
            </Col>
            <Col xs={24} md={10}>
              <Search placeholder="Nhập tên hoặc mã nhân viên..." value={searchValue} onChange={e => setSearchValue(e.target.value)} onPressEnter={handleSearch} size="large" style={{ width: '100%' }} />
            </Col>
            <Col xs={24} md={6}>
              <Button type="primary" size="large" icon={<SearchOutlined />} onClick={handleSearch} loading={loading} style={{ width: '100%' }} disabled={!searchValue.trim()}>Tra cứu</Button>
            </Col>
          </Row>
        </Card>

        {loading && <Card style={{ ...cardStyle, textAlign: 'center', padding: 40 }}><Spin size="large" /><div style={{ marginTop: 16 }}><Text>Đang tra cứu...</Text></div></Card>}

        {!loading && employee && <div style={{ marginBottom: 32 }}><EmployeeInfoCard employee={employee} /></div>}

        {!loading && employee && <Card style={cardStyle}>
          <Title level={3} style={cardTitleStyle}><span style={{ color: '#1890ff', marginRight: 8 }}>🗂️</span>Danh sách tài sản được giao</Title>
            {assets.length > 0 ? (
            <Table dataSource={assets} columns={columns} rowKey="id" rowSelection={rowSelection} pagination={{ pageSize }} size="middle" />
          ) : <Result status="info" title="Không có tài sản" subTitle="Nhân viên này chưa được giao tài sản nào" />}
        </Card>}

        {!loading && !employee && searchValue && <Card style={cardStyle}><Result status="warning" title="Không tìm thấy nhân viên" subTitle="Vui lòng kiểm tra lại tên hoặc mã nhân viên" /></Card>}

        <Modal open={assetModalOpen} onCancel={() => setAssetModalOpen(false)} footer={null} width={1200} destroyOnClose centered bodyStyle={{ padding: 0, minHeight: 600 }}>
          {selectedAssetUuid && <PublicAssetDetail public_uuid={selectedAssetUuid} isModal />}
        </Modal>
      </div>
    </div>
  );
};

export default EmployeeAssetSearch; 
